import { useState } from 'react';
import { ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { useAddWork, useServices, useAssignees } from '../lib/queries';
import { ServicePicker, type ServiceChoice } from './ServicePicker';
import { Alert, Button, Field, Select } from './ui';

interface Props {
  orderId: string;
  /** Whether this person may hand the job to someone while adding it. */
  mayAssign: boolean;
  onDone: () => void;
}

/**
 * One more job on a car that is already here.
 *
 * The same picker as at the door, so a job the catalogue does not have can be
 * typed in. A car that was ready goes back into work on the server's side.
 */
export function AddWork({ orderId, mayAssign, onDone }: Props) {
  const { t } = useI18n();
  const errorText = useApiError();
  const { data: services } = useServices();
  const assignees = useAssignees(mayAssign);
  const add = useAddWork(orderId);

  const [choice, setChoice] = useState<ServiceChoice | null>(null);
  const [who, setWho] = useState<string | null>(null);

  const isNamed = choice !== null
    && (choice.kind === 'service' || choice.customName.trim().length > 0);

  const submit = () => {
    if (!choice || !isNamed) return;
    add.mutate([{
      serviceId: choice.kind === 'service' ? choice.serviceId : null,
      customName: choice.kind === 'custom' ? choice.customName.trim() : null,
      quantity: 1,
      performedByAdminId: who,
    }], { onSuccess: onDone });
  };

  return (
    <div className="mt-3 space-y-2.5 rounded-xl border border-brand/30 bg-raised p-3">
      {add.error instanceof ApiError && (
        <Alert>{errorText(add.error) ?? add.error.message}</Alert>
      )}

      <ServicePicker
        label={t('neworder.service')}
        services={services?.items ?? []}
        value={choice}
        onChange={setChoice}
      />

      {choice?.kind === 'custom' && (
        <Field
          label={t('floor.oneOffName')}
          value={choice.customName}
          autoFocus={choice.customName === ''}
          onChange={(event) => setChoice({ kind: 'custom', customName: event.target.value })}
        />
      )}

      {mayAssign && (
        <Select
          label={t('floor.who')}
          value={who ?? ''}
          onChange={(event) => setWho(event.target.value || null)}
        >
          <option value="">{t('floor.unassigned')}</option>
          {assignees(who).map((person) => (
            <option key={person.id} value={person.id}>
              {person.firstName} {person.lastName}
            </option>
          ))}
        </Select>
      )}

      <div className="flex gap-2">
        <Button className="flex-1" disabled={!isNamed} loading={add.isPending} onClick={submit}>
          {t('floor.addWorkConfirm')}
        </Button>
        <Button variant="ghost" onClick={onDone}>{t('common.cancel')}</Button>
      </div>
    </div>
  );
}
